import { FolderContent, Folder as FolderType, UUID } from '@/api/generatedApi';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useContextMenu } from '@/hooks/useContextMenu';
import { useGlobalDeleteState } from '@/hooks/useGlobalDeleteState';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { useAssetUploadContext } from '@/services/assetUpload/useAssetUploadContext';
import { createUseStyles } from '@/services/createUseStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { ApiErrorDisplay } from '@/types/apiError';
import { ContextMenuOption, ALL_CONTEXT_MENU_OPTIONS } from '@/types/contextMenuOptions';
import { insertMetadataColumnsAfterDetails } from '@/utils/metadata';
import { alpha, Box, Theme } from '@mui/material';
import { useGridApiRef, GridApiPro, GridColDef, GridRowClassNameParams } from '@mui/x-data-grid-pro';
import { useEffect, useMemo } from 'react';
import { ContentsTableContextMenu } from '@/components/ContextMenu/ContentsTableContextMenu';
import { DataGridLazy } from '../DataGrid/DataGridLazy';
import { DragDropUpload } from '../Upload/DragDropUpload/DragDropUpload';
import { ZeroStateOverlay } from '../ZeroStateUpload/ZeroStateOverlay';
import { getBaseColumns } from './columnDefinitions';
import { DATA_GRID_LAZY_PAGE_SIZE } from '../DataGrid/definitions';
import { DraggableRow } from '../DataGrid/DraggableRow';
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { BulkSelectionProvider } from './BulkSelectionContext';
import { ContentsTableToolbar } from './ContentsTableToolbar';
import { DraggableFolderContentRow } from '../AssetsPage/DraggableFolderContentRow';
import { useContentDragOptional } from '../AssetsPage/ContentDragContext';
import { useScrollPositionRestore } from '@/hooks/useScrollPositionRestore';
import { useGridStateParameters } from '@/hooks/useGridStateParameters';
import { useNavigationType } from 'react-router-dom';

const useStyles = createUseStyles((theme: Theme) => ({
  DataGrid: {
    borderRadius: '6px !important',
    '& .MuiDataGrid-cell': {
      lineHeight: 'unset !important',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    '& .MuiDataGrid-cellCheckbox': {
      cursor: 'default',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none !important',
    },
    '& .MuiDataGrid-row.folder-drop-target': {
      backgroundColor: `${alpha(theme.palette.info.main, 0.15)} !important`,
      outline: `2px solid ${alpha(theme.palette.info.main, 0.8)} !important`,
      outlineOffset: '-2px',
      '& .MuiDataGrid-cell': {
        backgroundColor: `${alpha(theme.palette.info.main, 0.15)} !important`,
      },
    },
    '& .folder-drop-target': {
      backgroundColor: `${alpha(theme.palette.info.main, 0.15)} !important`,
      outline: `2px solid ${alpha(theme.palette.info.main, 0.8)} !important`,
    },
  },
  tableContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  dataGridWrapper: {
    flex: 1,
    minWidth: 0,
    height: '100%',
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    padding: '24px',
  },
}));

export const ContentsTable = ({
  fetchContents,
  isLoading,
  error,
  currentFolder,
  isFolderEmpty,
  isSearch,
  enableBulkActions = true,
  contextMenuOptions = ALL_CONTEXT_MENU_OPTIONS,
  enableRowDragging = false,
  enableFolderDragging = false,
  onRowContextMenu,
  gridApiRef,
  customBaseColumns,
  metadataColumns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  isColumnDataLoading,
  showRootFolder,
  showCreateButton = true,
  showFiltersButton = true,
  onRefresh,
  toolbarActions,
  bulkActions,
}: {
  fetchContents: (parameters: {
    page: number;
    perPage: number;
  }) => Promise<{ contents: FolderContent[]; pageData: { total: number } }>;
  isLoading?: boolean;
  error?: ApiErrorDisplay;
  currentFolder?: FolderType;
  isFolderEmpty?: boolean;
  isSearch?: boolean;
  enableBulkActions?: boolean;
  contextMenuOptions?: ContextMenuOption[];
  enableRowDragging?: boolean;
  enableFolderDragging?: boolean;
  onRowContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  gridApiRef?: React.RefObject<GridApiPro | null>;
  customBaseColumns?: GridColDef[];
  metadataColumns?: GridColDef[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  isColumnDataLoading?: boolean;
  showRootFolder?: boolean;
  showCreateButton?: boolean;
  showFiltersButton?: boolean;
  onRefresh?: () => void;
  toolbarActions?: React.ReactNode;
  bulkActions?: React.ReactNode;
}) => {
  const c = useStyles();
  const { getTenantId } = useTenants();
  const folderDragContext = useContentDragOptional();

  const { setTargetFolderId, handleFileUpload, handleFolderUpload, dropzoneProps } = useAssetUploadContext();

  useEffect(() => {
    setTargetFolderId(currentFolder?.id);
  }, [currentFolder?.id, setTargetFolderId]);

  const { permissions } = useGetCurrentUser();
  const { isAnyDeletePending } = useGlobalDeleteState();

  const internalGridApiReference = useGridApiRef();
  const gridApi = gridApiRef ?? internalGridApiReference;

  const navigationType = useNavigationType();
  useScrollPositionRestore({
    apiReference: gridApi,
    folderKey: currentFolder?.id ?? 'root',
    shouldRestore: navigationType === 'POP',
  });

  const { sortModel, filterModel, onSortModelChange, onFilterModelChange } = useGridStateParameters();

  const { selectedRows, isLoadingAll, handleSelectionChange, clearSelection } = useBulkSelection<
    FolderContent,
    { contents: FolderContent[] }
  >({
    fetchContents,
    dataKey: 'contents',
    pageSize: DATA_GRID_LAZY_PAGE_SIZE,
    gridApi,
  });

  useEffect(() => {
    clearSelection();
  }, [currentFolder?.id]);

  const hasContextMenu = contextMenuOptions.length > 0;

  const canUseBulkActionsSidebar = enableBulkActions && [permissions.canDeleteAssets].some(Boolean);
  const canUseDragDropUpload =
    contextMenuOptions.includes('create') && !isSearch && [permissions.canUploadAssets].some(Boolean);

  const allowedContextMenuOptions = contextMenuOptions.filter(option => {
    if (option === 'create') {
      return [permissions.canUploadAssets, permissions.canCreateFolders].some(Boolean);
    }
    if (option === 'delete') {
      return [permissions.canDeleteAssets, permissions.canDeleteFolders].some(Boolean);
    }
    return true;
  });

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<FolderContent>({
    canUseContextMenu: hasContextMenu,
    getRowById: (rowId: UUID) => gridApi.current?.getRow<FolderContent>(rowId) ?? undefined,
  });

  const dropTargetFolderId = folderDragContext?.dropTargetFolderId;
  const draggedContents = folderDragContext?.draggedContents ?? [];

  const getRowClassName = (parameters: GridRowClassNameParams<FolderContent>) => {
    if (!enableFolderDragging || !dropTargetFolderId || draggedContents.length === 0) {
      return '';
    }

    const row = parameters.row;
    if (row?.contentType !== 'folder') {
      return '';
    }

    const isBeingDragged = draggedContents.some(c => c.id === row.id);
    if (row.id === dropTargetFolderId && !isBeingDragged) {
      return 'folder-drop-target';
    }

    return '';
  };

  const columns = useMemo(() => {
    const columnsToUse = customBaseColumns ?? getBaseColumns();
    return insertMetadataColumnsAfterDetails(columnsToUse, metadataColumns ?? []);
  }, [customBaseColumns, metadataColumns]);

  if (!getTenantId()) {
    return;
  }

  const gridContent = (
    <DragDropUpload dropzoneProps={dropzoneProps} disabled={!canUseDragDropUpload}>
      <Box
        className={c.dataGridWrapper}
        onContextMenu={onRowContextMenu ?? handleGridContextMenu}
        data-testid={AssetsPageObjectModel.DataGridWrapper}
      >
        <BulkSelectionProvider
          value={{
            selectedRows,
            isLoadingAll,
            clearSelection,
            selectionCount: selectedRows.length,
          }}
        >
          <DataGridLazy<FolderContent, { contents: FolderContent[] }>
            apiRef={gridApi}
            className={c.DataGrid}
            columns={columns}
            sx={{
              '& .MuiBadge-root .MuiBadge-badge': { display: 'none' },
            }}
            initialState={{ pinnedColumns: { left: ['name'], right: ['__check__'] } }}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            fetchRows={fetchContents}
            dataKey='contents'
            rowHeight={80}
            columnHeaderHeight={40}
            loading={isLoading || isColumnDataLoading || isAnyDeletePending}
            checkboxSelection={canUseBulkActionsSidebar}
            onRowSelectionModelChange={handleSelectionChange}
            error={error}
            density='compact'
            getRowClassName={getRowClassName}
            disableRowSelectionOnClick
            hideFooter={isFolderEmpty}
            showToolbar
            slots={{
              toolbar: ContentsTableToolbar,
              noRowsOverlay: canUseDragDropUpload ? ZeroStateOverlay : undefined,
              ...(enableRowDragging && { row: DraggableRow }),
              ...(enableFolderDragging && { row: DraggableFolderContentRow }),
            }}
            slotProps={{
              toolbar: {
                folderId: currentFolder?.id,
                showRootFolder,
                bulkActions,
                showCreateButton,
                showFiltersButton,
                onRefresh,
                toolbarActions,
              } as {
                folderId?: string;
                showRootFolder?: boolean;
                bulkActions?: React.ReactNode;
                showCreateButton?: boolean;
                showFiltersButton?: boolean;
                onRefresh?: () => void;
                toolbarActions?: React.ReactNode;
              },
              footer: {
                sx: {
                  minHeight: '27px',
                  padding: '0 10px',
                },
              },
              footerRowCount: { sx: { margin: 0 } },
              row: onRowContextMenu
                ? {
                    onContextMenu: onRowContextMenu,
                  }
                : undefined,
            }}
            localeText={{ footerTotalRows: 'Rows:' }}
          />
        </BulkSelectionProvider>
      </Box>
    </DragDropUpload>
  );

  return (
    <Box className={c.tableContainer}>
      {gridContent}
      {hasContextMenu && (
        <ContentsTableContextMenu<FolderContent>
          anchorPosition={contextMenuPosition}
          onClose={handleCloseContextMenu}
          clickedItem={contextMenuClickedItem}
          clickedField={contextMenuClickedField}
          columns={columns}
          parentFolder={currentFolder}
          handleFileUpload={handleFileUpload}
          handleFolderUpload={handleFolderUpload}
          setTargetFolderId={setTargetFolderId}
          isSearch={isSearch}
          allowedOptions={allowedContextMenuOptions}
          selectedRows={selectedRows}
          onBulkActionComplete={clearSelection}
        />
      )}
    </Box>
  );
};
